import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, Users, Phone, CalendarRange, StickyNote } from "lucide-react";
import apiClient, { formatApiErrorDetail } from "@/lib/api";
import { formatDate } from "@/components/booking/bookingMeta";
import {
  TENANT_STATUS_LABELS, TENANT_STATUS_STYLES,
  RENTAL_PERIOD_LABELS, RENTAL_PERIOD_OPTIONS,
} from "@/components/property/tenantMeta";

const inputCls =
  "w-full rounded-xl border border-border bg-secondary/40 px-3.5 py-2.5 text-sm text-foreground outline-none focus:ring-2 focus:ring-primary focus:bg-background transition-colors";

const EMPTY = { tenant_name: "", phone: "", start_date: "", end_date: "", rental_period: "monthly", notes: "" };

function Field({ label, required, children, testid }) {
  return (
    <div data-testid={testid}>
      <label className="text-sm font-medium text-foreground">
        {label} {required && <span className="text-destructive">*</span>}
      </label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

export default function TenantManager({ room }) {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get(`/rooms/${room.id}/tenants`);
      setTenants(data || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Gagal memuat penghuni");
    } finally {
      setLoading(false);
    }
  }, [room.id]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openAdd = () => { setEditing(null); setForm(EMPTY); setFormError(""); setShowForm(true); };
  const openEdit = (t) => {
    setEditing(t);
    setForm({
      tenant_name: t.tenant_name || "",
      phone: t.phone || "",
      start_date: t.start_date || "",
      end_date: t.end_date || "",
      rental_period: t.rental_period || "monthly",
      notes: t.notes || "",
    });
    setFormError("");
    setShowForm(true);
  };

  const submit = async () => {
    setFormError("");
    if (!form.tenant_name.trim()) { setFormError("Nama penghuni wajib diisi."); return; }
    if (!form.start_date) { setFormError("Tanggal mulai sewa wajib diisi."); return; }
    if (form.end_date && form.end_date < form.start_date) { setFormError("Tanggal berakhir tidak boleh sebelum tanggal mulai."); return; }
    const payload = {
      tenant_name: form.tenant_name.trim(),
      phone: form.phone.trim() || null,
      start_date: form.start_date,
      end_date: form.end_date || null,
      rental_period: form.rental_period,
      notes: form.notes.trim() || null,
    };
    setSaving(true);
    try {
      if (editing) {
        await apiClient.put(`/tenants/${editing.id}`, payload);
        toast.success("Data penghuni diperbarui");
      } else {
        await apiClient.post(`/rooms/${room.id}/tenants`, payload);
        toast.success("Penghuni ditambahkan");
      }
      setShowForm(false);
      load();
    } catch (err) {
      setFormError(formatApiErrorDetail(err.response?.data?.detail) || "Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Hapus penghuni "${t.tenant_name}"? Data ini akan dihapus permanen.`)) return;
    try {
      await apiClient.delete(`/tenants/${t.id}`);
      toast.success("Penghuni dihapus");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Gagal menghapus");
    }
  };

  return (
    <div data-testid="tenant-manager">
      <div className="flex items-center justify-between gap-3 mb-4">
        <p className="text-sm text-muted-foreground">Kelola data penghuni & periode sewa. Riwayat penghuni lama tetap tersimpan.</p>
        {!showForm && (
          <button type="button" onClick={openAdd} data-testid="tenant-add-button"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent text-accent-foreground text-sm font-semibold hover:scale-105 transition-transform shrink-0">
            <Plus size={15} /> Tambah Penghuni
          </button>
        )}
      </div>

      {showForm && (
        <div className="rounded-2xl border border-border bg-secondary/20 p-4 mb-5 space-y-3" data-testid="tenant-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Nama Penghuni" required testid="tenant-field-name">
              <input className={inputCls} value={form.tenant_name} onChange={(e) => set("tenant_name", e.target.value)}
                data-testid="tenant-input-name" placeholder="Nama lengkap" />
            </Field>
            <Field label="Nomor Telepon" testid="tenant-field-phone">
              <input className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)}
                data-testid="tenant-input-phone" placeholder="mis. 0812xxxx" />
            </Field>
            <Field label="Tanggal Mulai Sewa" required testid="tenant-field-start">
              <input type="date" className={inputCls} value={form.start_date} onChange={(e) => set("start_date", e.target.value)}
                data-testid="tenant-input-start" />
            </Field>
            <Field label="Tanggal Berakhir Sewa" testid="tenant-field-end">
              <input type="date" className={inputCls} value={form.end_date} min={form.start_date || undefined}
                onChange={(e) => set("end_date", e.target.value)} data-testid="tenant-input-end" />
            </Field>
            <Field label="Periode Sewa" testid="tenant-field-period">
              <select className={inputCls} value={form.rental_period} onChange={(e) => set("rental_period", e.target.value)}
                data-testid="tenant-input-period">
                {RENTAL_PERIOD_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </Field>
            <Field label="Catatan" testid="tenant-field-notes">
              <input className={inputCls} value={form.notes} onChange={(e) => set("notes", e.target.value)}
                data-testid="tenant-input-notes" placeholder="Opsional" />
            </Field>
          </div>
          {formError && <p className="text-sm text-destructive" data-testid="tenant-form-error">{formError}</p>}
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={() => setShowForm(false)} data-testid="tenant-cancel-button"
              className="px-4 py-2 rounded-full border border-border text-sm font-semibold hover:bg-secondary/60 transition-colors">
              Batal
            </button>
            <button type="button" onClick={submit} disabled={saving} data-testid="tenant-save-button"
              className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-primary text-primary-foreground text-sm font-semibold hover:scale-105 transition-transform disabled:opacity-60">
              {saving ? <Loader2 size={15} className="animate-spin" /> : null} {editing ? "Simpan Perubahan" : "Simpan"}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="py-8 grid place-items-center"><Loader2 className="animate-spin text-primary" size={22} /></div>
      ) : tenants.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-secondary/30 p-8 text-center" data-testid="tenant-empty">
          <Users size={24} className="mx-auto mb-2 text-primary" />
          <p className="text-sm text-muted-foreground">Belum ada data penghuni untuk kamar ini.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="tenant-list">
          {tenants.map((t, i) => (
            <div key={t.id} data-testid={`tenant-item-${i}`}
              className="rounded-2xl border border-border bg-card p-4 flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold" data-testid={`tenant-name-${i}`}>{t.tenant_name}</span>
                  <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full border ${TENANT_STATUS_STYLES[t.status] || ""}`} data-testid={`tenant-status-${i}`}>
                    {TENANT_STATUS_LABELS[t.status] || t.status}
                  </span>
                </div>
                <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                  {t.phone && <span className="inline-flex items-center gap-1.5"><Phone size={13} /> {t.phone}</span>}
                  <span className="inline-flex items-center gap-1.5" data-testid={`tenant-period-${i}`}>
                    <CalendarRange size={13} /> {formatDate(t.start_date)} — {t.end_date ? formatDate(t.end_date) : "Tanpa batas"}
                    <span className="text-xs">({RENTAL_PERIOD_LABELS[t.rental_period] || t.rental_period})</span>
                  </span>
                </div>
                {t.notes && (
                  <p className="mt-1.5 text-sm text-muted-foreground inline-flex items-start gap-1.5">
                    <StickyNote size={13} className="mt-0.5 shrink-0" /> {t.notes}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button type="button" onClick={() => openEdit(t)} data-testid={`tenant-edit-${i}`}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-border text-sm font-semibold text-primary hover:bg-secondary/60 transition-colors">
                  <Pencil size={13} /> Edit
                </button>
                <button type="button" onClick={() => remove(t)} data-testid={`tenant-delete-${i}`}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-border text-sm font-semibold text-destructive hover:bg-destructive/10 transition-colors">
                  <Trash2 size={13} /> Hapus
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
